import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Boxes,
  Tag,
  Shirt,
  Layers,
  Clock,
  Scale,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison simple et rapide à Bailly ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Une livraison de fournitures de déménagement simple et
              rapide {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que l&apos;organisation d&apos;un déménagement
              demande beaucoup d&apos;énergie, nous avons conçu un
              service logistique de proximité qui s&apos;adapte à votre
              emploi du temps.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Secteurs desservis",
                body: "Notre équipe livre vos cartons directement chez vous à Bailly, que vous résidiez dans le centre ou les quartiers pavillonnaires, ainsi que dans les communes limitrophes comme Noisy-le-Roi, Rocquencourt, Versailles ou Louveciennes.",
                Icon: Truck,
              },
              {
                title: "Praticité maximale",
                body: "Vos fournitures vous sont livrées à plat et parfaitement propres. Vous évitez ainsi la corvée de transport des cartons volumineux dans votre véhicule personnel et gagnez un temps précieux pour vos préparatifs.",
                Icon: Calendar,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons penderie ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Cartons penderie
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Cartons penderie pour vêtements : protégez votre garde-robe
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Déménager ses vêtements est souvent une source de corvée
              et de vêtements froissés. Pour résoudre ce problème, nous
              mettons à votre disposition des cartons penderie
              spécialement pensés pour le textile.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Prêt à suspendre",
                  body: "Équipés d'une tringle amovible ultra-résistante, ces cartons de grande hauteur vous permettent de transférer vos manteaux, costumes, robes et vestes directement depuis votre armoire sans les décrocher de leurs cintres.",
                  Icon: Shirt,
                },
                {
                  title: "Protection et gain de temps",
                  body: "Vos vêtements restent suspendus et protégés de la poussière pendant toute la durée du voyage. À l'arrivée dans votre nouvelle habitation à Bailly, il ne vous reste plus qu'à les replacer directement dans votre dressing, sans passer par la case repassage.",
                  Icon: ShieldCheck,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-demenagement?categorie=penderie"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-bailly-cta-penderie"
            >
              <Shirt className="h-4 w-4" strokeWidth={2.6} />
              Voir nos cartons penderie
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Shirt className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Zéro repassage à l&apos;arrivée
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Vos vêtements voyagent suspendus, à l&apos;abri de la
              poussière. À l&apos;ouverture, vous les replacez
              directement dans le dressing.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Tringle métallique amovible
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Grande hauteur pour manteaux
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cintres conservés
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Protection anti-poussière
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Conseils sereins ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos conseils
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Préparer son déménagement sereinement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Anticiper et structurer l&apos;emballage de vos pièces est
            la clé absolue pour faire baisser le stress du jour J.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "La méthode pièce par pièce",
              body: "Commencez à emballer plusieurs semaines à l'avance les objets que vous utilisez le moins (livres, décorations, vêtements hors saison).",
              Icon: Clock,
            },
            {
              title: "L'étiquetage intelligent",
              body: "Notez clairement sur chaque carton la pièce de destination (Cuisine, Chambre 1, Salon) et un résumé rapide de son contenu. Cela facilitera grandement le travail des déménageurs au moment du déchargement.",
              Icon: Tag,
            },
            {
              title: "La gestion du poids",
              body: "Utilisez nos petits cartons renforcés pour les éléments lourds comme les livres, et réservez les grands volumes pour le linge de maison ou les objets légers afin de ne pas surcharger les boîtes.",
              Icon: Scale,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-bailly-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir les kits complets
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-bailly-cta-accessoires"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; accessoires
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-bailly-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
